import React, { useState, useRef, useEffect } from "react";
import { api, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { formatDuration, initials, formatDate } from "@/lib/format";
import MentionTextarea, { renderBodyWithMentions } from "@/components/MentionTextarea";
import {
  Activity, MessageSquare, Send, Paperclip, X, FileText, Image as ImageIcon,
  Clock, Tag, User, Calendar, Link2, ListTree, Trash2, Edit2, Check,
} from "lucide-react";

// Render comment body — preserves whitespace, highlights ``` code blocks, mentions
function CommentBody({ body, users }) {
  if (!body) return null;
  const parts = body.split(/(```[\s\S]*?```)/g);
  return (
    <div className="text-sm whitespace-pre-wrap break-words" data-testid="comment-body">
      {parts.map((part, i) => {
        if (part.startsWith("```") && part.endsWith("```")) {
          const inner = part.slice(3, -3).replace(/^\n/, "");
          return (
            <pre key={i} className="my-2 bg-neutral-950 text-neutral-100 p-3 rounded-sm overflow-x-auto font-mono text-xs">
              <code>{inner}</code>
            </pre>
          );
        }
        return <span key={i}>{renderBodyWithMentions(part, users)}</span>;
      })}
    </div>
  );
}

function ActivityIcon({ kind }) {
  const cls = "w-3.5 h-3.5";
  switch (kind) {
    case "commented": return <MessageSquare className={cls} />;
    case "status_changed": return <Tag className={cls} />;
    case "assignee_changed": return <User className={cls} />;
    case "priority_changed": return <Tag className={cls} />;
    case "due_date_changed":
    case "start_date_changed": return <Calendar className={cls} />;
    case "time_logged": return <Clock className={cls} />;
    case "attachment_added": return <Paperclip className={cls} />;
    case "subtask_added": return <ListTree className={cls} />;
    case "dependency_added":
    case "dependency_removed": return <Link2 className={cls} />;
    default: return <Activity className={cls} />;
  }
}

function describeActivity(a, ctx) {
  const { statusMap, userMap } = ctx;
  const d = a.data || {};
  switch (a.kind) {
    case "created": return "created this task";
    case "title_changed": return `renamed to "${d.to}"`;
    case "description_changed": return "updated the description";
    case "status_changed": {
      const from = statusMap[d.from]?.name || "—";
      const to = statusMap[d.to]?.name || "—";
      return `changed status: ${from} → ${to}`;
    }
    case "assignee_changed": {
      const from = d.from ? (userMap[d.from]?.name || "someone") : "Unassigned";
      const to = d.to ? (userMap[d.to]?.name || "someone") : "Unassigned";
      return `re-assigned: ${from} → ${to}`;
    }
    case "assignees_changed": {
      const added = (d.added || []).map((id) => userMap[id]?.name).filter(Boolean);
      const removed = (d.removed || []).map((id) => userMap[id]?.name).filter(Boolean);
      const parts = [];
      if (added.length) parts.push(`added ${added.join(", ")}`);
      if (removed.length) parts.push(`removed ${removed.join(", ")}`);
      return parts.length ? parts.join(" · ") : "updated assignees";
    }
    case "priority_changed": return `set priority: ${d.from || "—"} → ${d.to}`;
    case "due_date_changed":
      return d.to ? `set due date to ${formatDate(d.to)}` : "cleared the due date";
    case "start_date_changed":
      return d.to ? `set start date to ${formatDate(d.to)}` : "cleared the start date";
    case "time_logged":
      return `logged ${formatDuration(d.duration_seconds)}${d.note ? ` — ${d.note}` : ""}`;
    case "attachment_added": return `attached "${d.filename}"`;
    case "subtask_added": return `added subtask "${d.subtask_title}"`;
    case "dependency_added": return `${d.relation === "blocks" ? "now blocks" : "is now blocked by"} "${d.other_title}"`;
    case "dependency_removed": return "removed a dependency";
    default: return a.kind;
  }
}

export default function ActivityFeed({ task, list, users, currentUserId, canEdit, onChange }) {
  const [activity, setActivity] = useState([]);
  const [attMap, setAttMap] = useState({});
  const [body, setBody] = useState("");
  const [mentionedIds, setMentionedIds] = useState([]);
  const [busy, setBusy] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editBody, setEditBody] = useState("");
  const [editMentions, setEditMentions] = useState([]);
  const fileRef = useRef(null);
  const mentionRef = useRef(null);
  const token = typeof window !== "undefined" ? localStorage.getItem("orbit_token") : "";

  const statuses = list?.statuses || [];
  const statusMap = Object.fromEntries(statuses.map((s) => [s.id, s]));
  const userMap = Object.fromEntries(users.map((u) => [u.id, u]));

  const refresh = async () => {
    try {
      const { data } = await api.get(`/tasks/${task.id}/activity`);
      setActivity(data.activity);
      setAttMap(Object.fromEntries((data.attachments || []).map((a) => [a.id, a])));
    } catch (e) {
      // silent
    }
  };

  useEffect(() => { refresh(); }, [task.id]);

  const onPickFile = () => fileRef.current?.click();

  const onFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post(`/tasks/${task.id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(data.attachment);
      }
      setPendingFiles((p) => [...p, ...uploaded]);
      onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removePending = (id) => setPendingFiles((p) => p.filter((f) => f.id !== id));

  const submitComment = async (e) => {
    e.preventDefault();
    if (!body.trim() && pendingFiles.length === 0) return;
    setBusy(true);
    try {
      await api.post(`/tasks/${task.id}/comments`, {
        body: body.trim() || "(attachment)",
        attachment_ids: pendingFiles.map((f) => f.id),
        mentioned_user_ids: mentionedIds,
      });
      setBody("");
      setMentionedIds([]);
      setPendingFiles([]);
      mentionRef.current?.reset();
      await refresh();
      onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to post comment");
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async () => {
    if (!editBody.trim()) { setEditingId(null); return; }
    try {
      await api.patch(`/comments/${editingId}`, { body: editBody.trim(), mentioned_user_ids: editMentions });
      setEditingId(null);
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    }
  };

  const deleteComment = async (id) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await api.delete(`/comments/${id}`);
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  return (
    <div className="space-y-4" data-testid="activity-feed">
      {/* Composer */}
      {canEdit && (
        <div className="bg-white border border-neutral-200 rounded-sm p-3" data-testid="comment-composer">
          <form onSubmit={submitComment} className="space-y-2">
            <MentionTextarea
              ref={mentionRef}
              value={body}
              onChange={(v, ids) => { setBody(v); setMentionedIds(ids); }}
              users={users}
              placeholder="Write a comment… type @ to mention someone, ``` for code"
              className="rounded-sm border-neutral-200 min-h-[60px] text-sm"
              dataTestId="comment-input"
            />
            {pendingFiles.length > 0 && (
              <div className="flex flex-wrap gap-1.5" data-testid="comment-pending-attachments">
                {pendingFiles.map((f) => (
                  <div key={f.id} className="flex items-center gap-1.5 bg-neutral-100 rounded-sm px-2 py-1 text-xs">
                    {(f.content_type || "").startsWith("image/") ? <ImageIcon className="w-3 h-3" /> : <FileText className="w-3 h-3" />}
                    <span className="truncate max-w-[120px]">{f.filename}</span>
                    <button type="button" onClick={() => removePending(f.id)} className="text-neutral-400 hover:text-red-600">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-center gap-2">
              <input ref={fileRef} type="file" multiple className="hidden" onChange={onFileChange} data-testid="comment-file-input" />
              <Button
                type="button"
                variant="outline"
                onClick={onPickFile}
                disabled={uploading}
                className="h-8 rounded-sm text-xs"
                data-testid="comment-attach-button"
              >
                <Paperclip className="w-3.5 h-3.5 mr-1" />{uploading ? "Uploading…" : "Attach"}
              </Button>
              <div className="font-mono-data text-[10px] text-neutral-400 ml-auto">Cmd+Enter to send</div>
              <Button
                type="submit"
                disabled={busy || (!body.trim() && pendingFiles.length === 0)}
                className="h-8 rounded-sm bg-neutral-950 hover:bg-neutral-800 text-xs"
                data-testid="comment-submit"
              >
                <Send className="w-3.5 h-3.5 mr-1" />Comment
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Feed */}
      <div className="space-y-2" data-testid="activity-list">
        {activity.length === 0 && (
          <div className="text-xs text-neutral-500 text-center py-6 bg-white border border-neutral-200 rounded-sm">No activity yet.</div>
        )}
        {activity.map((a) => {
          const u = userMap[a.user_id];
          const isComment = a.kind === "commented";
          if (isComment) {
            const myOwn = a.user_id === currentUserId;
            const isEditing = editingId === a.id;
            const attached = (a.attachment_ids || []).map((id) => attMap[id]).filter(Boolean);
            return (
              <div key={a.id} className="bg-white border border-neutral-200 rounded-sm p-3" data-testid={`comment-${a.id}`}>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-6 h-6 rounded-full bg-neutral-950 text-white text-[9px] font-bold flex items-center justify-center">{initials(u?.name)}</div>
                  <span className="text-sm font-semibold">{u?.name || "Unknown"}</span>
                  <span className="font-mono-data text-[10px] text-neutral-400">{formatDate(a.created_at)}</span>
                  {a.edited && <span className="font-mono-data text-[10px] text-neutral-400">· edited</span>}
                  {myOwn && !isEditing && (
                    <div className="ml-auto flex items-center gap-1 opacity-0 group-hover:opacity-100">
                      <button onClick={() => { setEditingId(a.id); setEditBody(a.body); setEditMentions(a.mentioned_user_ids || []); }} className="text-neutral-400 hover:text-neutral-900" data-testid={`comment-edit-${a.id}`}>
                        <Edit2 className="w-3 h-3" />
                      </button>
                      <button onClick={() => deleteComment(a.id)} className="text-neutral-400 hover:text-red-600" data-testid={`comment-delete-${a.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                </div>
                {isEditing ? (
                  <div className="space-y-2">
                    <MentionTextarea
                      value={editBody}
                      onChange={(v, ids) => { setEditBody(v); setEditMentions(ids); }}
                      users={users}
                      className="rounded-sm min-h-[60px] text-sm"
                      dataTestId={`comment-edit-input-${a.id}`}
                    />
                    <div className="flex gap-2">
                      <Button onClick={saveEdit} className="h-7 rounded-sm bg-neutral-950 hover:bg-neutral-800 text-xs" data-testid={`comment-save-${a.id}`}>
                        <Check className="w-3 h-3 mr-1" />Save
                      </Button>
                      <Button variant="outline" onClick={() => setEditingId(null)} className="h-7 rounded-sm text-xs">Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <CommentBody body={a.body} users={users} />
                )}
                {attached.length > 0 && (
                  <div className="grid grid-cols-3 gap-1.5 mt-2" data-testid={`comment-attachments-${a.id}`}>
                    {attached.map((att) => {
                      const isImg = (att.content_type || "").startsWith("image/");
                      const url = `${API_BASE}/attachments/${att.id}/download?auth=${encodeURIComponent(token)}`;
                      return (
                        <a key={att.id} href={url} target="_blank" rel="noopener noreferrer" className="block bg-neutral-50 border border-neutral-200 rounded-sm overflow-hidden hover:border-neutral-400 transition-colors">
                          {isImg ? (
                            <img src={url} alt={att.filename} className="w-full h-20 object-cover" />
                          ) : (
                            <div className="h-20 flex items-center justify-center">
                              <FileText className="w-6 h-6 text-neutral-400" />
                            </div>
                          )}
                          <div className="px-2 py-1 text-[10px] truncate">{att.filename}</div>
                        </a>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }
          return (
            <div key={a.id} className="flex items-start gap-2 text-xs px-3 py-1.5" data-testid={`activity-${a.id}`}>
              <div className="w-5 h-5 rounded-full bg-neutral-100 text-neutral-500 flex items-center justify-center shrink-0 mt-0.5">
                <ActivityIcon kind={a.kind} />
              </div>
              <div className="flex-1 min-w-0">
                <span className="font-semibold text-neutral-900">{u?.name || "Someone"}</span>{" "}
                <span className="text-neutral-600">{describeActivity(a, { statusMap, userMap })}</span>
              </div>
              <span className="font-mono-data text-[10px] text-neutral-400">{formatDate(a.created_at)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
